/**
 * Guard for the agent's active-question object before it renders as tappable
 * chips. Same stakes as the suggestions sanitizer in ChatConversation: a
 * tapped chip sends a gateway turn (outright in single-select, via the
 * confirm chip in multi) unless it is structured — an `action` chip dispatches
 * client behaviour and an `accept` chip posts a target-price confirmation — so
 * junk must never render.
 */
import type { ChatQuestion, ImportOptionAction, QuestionOption } from '../../services/chatStream';
import { ALL_STAGE_KEYS } from '../../services/targetPriceCard';

// Local mirror of ImportOptionAction's values: only these UI flows may ride
// on an option object; anything else degrades the entry to a bare string
// label (same doctrine as the backend's server-side allowlist).
// `open_thesis_builder` is the target-price hard-gate's refusal chip (Part 2
// Task 6 emitted it; Task 12 carries in the missing client half): tapping it
// opens the structured build_thesis turn instead of sending the label.
// `refresh_research` is the research-staleness card's confirm chip
// (research-freshness spec §C.4a): it dispatches the structured refresh turn.
// Keep this list in step with the backend's `OptionAction` literal — a name
// missing here degrades the chip to a plain label that sends prose, and a name
// missing THERE breaks the messages endpoint that serves the chip.
const IMPORT_OPTION_ACTIONS: readonly ImportOptionAction[] = [
  'open_import_picker',
  'open_import_sheet',
  'discard_import',
  'open_thesis_builder',
  'refresh_research',
];

/** The wizard stage keys a chip tap confirms (M5). Client twin of the
 * backend's _clean_accept: closed to ALL_STAGE_KEYS and de-duplicated, so a
 * junk key drops itself and an all-junk list degrades the option to a bare
 * label — i.e. to the ordinary text-turn chip. */
function cleanAccept(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const key of raw) {
    if (typeof key === 'string' && ALL_STAGE_KEYS.includes(key) && !out.includes(key)) {
      out.push(key);
    }
  }
  return out;
}

export function sanitizeQuestion(raw: unknown): ChatQuestion | null {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as { prompt?: unknown; options?: unknown; multi?: unknown };
  if (!Array.isArray(candidate.options)) return null;
  // `multi` marks a pick-many step: the chips toggle instead of auto-sending
  // (see QuestionChips), so more options are affordable on screen. Anything
  // but a real boolean degrades to single-select.
  const multi = typeof candidate.multi === 'boolean' ? candidate.multi : false;
  const cap = multi ? 6 : 4;
  const seen = new Set<string>();
  const options: (string | QuestionOption)[] = [];
  for (const item of candidate.options) {
    if (typeof item !== 'string' && (typeof item !== 'object' || item === null)) continue;
    // Strings and object labels share one rule: trim, then require a letter
    // or digit. No minimum length — a question option can legitimately be
    // one char ("A", "B", or a single CJK word); this test is what rejects
    // placeholder junk ("...", "-").
    const rawLabel = typeof item === 'string' ? item : item.label;
    if (typeof rawLabel !== 'string') continue;
    const label = rawLabel.trim();
    if (!label || !/[\p{L}\p{N}]/u.test(label)) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    if (typeof item === 'object') {
      // An object entry is kept structured only when its action is on the
      // allowlist; unknown or missing actions degrade to the bare label so
      // the chip still renders and still sends (back-compat behavior).
      const action =
        typeof (item as { action?: unknown }).action === 'string'
          ? ((item as { action: string }).action as ImportOptionAction)
          : undefined;
      if (action && IMPORT_OPTION_ACTIONS.includes(action)) {
        // Carry the action's target symbol through (open_thesis_builder). The
        // backend sends it key-present as a string or null; anything else
        // (absent key, junk type) simply drops the field — the dispatch
        // resolves a missing symbol to "no symbol".
        const rawSymbol = (item as { symbol?: unknown }).symbol;
        const option: QuestionOption = { label, action };
        if (typeof rawSymbol === 'string' || rawSymbol === null) option.symbol = rawSymbol;
        options.push(option);
      } else {
        // The second structured shape (M5): an action-less option carrying the
        // wizard stage keys it confirms. Mutually exclusive with `action` —
        // an action chip dispatches client behaviour, an accept chip posts a
        // confirmation and sends NO turn.
        const accept = cleanAccept((item as { accept?: unknown }).accept);
        options.push(accept.length ? { label, accept } : label);
      }
    } else {
      options.push(label);
    }
    if (options.length === cap) break;
  }
  if (options.length < 2) return null;
  const prompt =
    typeof candidate.prompt === 'string' && candidate.prompt.trim()
      ? candidate.prompt.trim()
      : null;
  return { prompt, options, multi };
}
